/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Email-first identity gate for "Complete booking" (modeled on aegis-ao-rental_web, adapted to
 * Aegis.Back). Flow:
 *   email → if the account exists → ask password → sign in;
 *           if the email is free → new-driver WIZARD:
 *             1) driver license: upload front/back, auto-read (OCR / PDF417) and prefill;
 *             2) personal info (prefilled) + password → create the renter with the license.
 * On success it calls onAuthed() so the booking flow can continue (booking → card → agreement).
 */

import { useState, useRef, useEffect } from 'react';
import { X, ArrowLeft, Loader2, UploadCloud, CheckCircle2, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

type Step = 'email' | 'password' | 'license' | 'info';

interface Props {
  onClose: () => void;
  onAuthed: () => void;
}

interface Form {
  firstName: string; lastName: string; email: string; phoneNumber: string;
  dateOfBirth: string; address: string; city: string; state: string; zipCode: string;
  driverLicenseId: string; driverLicenseIssuingState: string; driverLicenseExpirationDate: string;
  password: string; confirmPassword: string;
}

const EMPTY: Form = {
  firstName: '', lastName: '', email: '', phoneNumber: '', dateOfBirth: '', address: '',
  city: '', state: '', zipCode: '', driverLicenseId: '', driverLicenseIssuingState: '',
  driverLicenseExpirationDate: '', password: '', confirmPassword: '',
};

/** Map a license OCR/barcode payload (either casing) into our form fields. */
function fromLicense(d: Record<string, any>): Partial<Form> {
  const g = (...keys: string[]) => { for (const k of keys) { const v = d[k]; if (v) return String(v).slice(0, 10) === '0001-01-01' ? '' : String(v); } return ''; };
  const date = (s: string) => (s && /\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s ? new Date(s).toISOString().slice(0, 10) : '');
  return {
    firstName: g('firstName', 'FirstName'),
    lastName: g('lastName', 'LastName'),
    dateOfBirth: date(g('dateOfBirth', 'DateOfBirth')),
    address: g('addressLine1', 'AddressLine1', 'address', 'Address'),
    city: g('city', 'City'),
    state: g('state', 'State', 'issuingState', 'IssuingState'),
    zipCode: g('zipCode', 'ZipCode', 'postalCode', 'PostalCode'),
    driverLicenseId: g('licenseNumber', 'LicenseNumber', 'documentNumber', 'DocumentNumber'),
    driverLicenseIssuingState: g('issuingState', 'IssuingState', 'state', 'State'),
    driverLicenseExpirationDate: date(g('expirationDate', 'ExpirationDate')),
  };
}

export default function BookingAuthModal({ onClose, onAuthed }: Props) {
  const { t } = useTranslation();
  const { signIn, applyAuth } = useAuth();

  const [step, setStep] = useState<Step>('email');
  // Registration matched a platform placeholder (e.g. a Turo guest): a code was texted to the phone on it.
  const [codePrompt, setCodePrompt] = useState<{ failed: boolean } | null>(null);
  const [phoneCode, setPhoneCode] = useState('');
  const [form, setForm] = useState<Form>(EMPTY);
  const [password, setPassword] = useState(''); // sign-in password (existing user)
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  // Raw OCR/barcode payloads kept so the register call can persist the original-language fields
  // (native-script name, place of birth, issuing authority, national id, categories, …).
  const [frontOcr, setFrontOcr] = useState<Record<string, any> | null>(null);
  const [backBarcode, setBackBarcode] = useState<Record<string, any> | null>(null);
  const [reading, setReading] = useState<'front' | 'back' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Phone-camera capture (QR + SSE push): the phone uploads photos, the server OCR's them
  // and pushes the parsed fields here — no polling.
  const [phoneSid, setPhoneSid] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => () => { esRef.current?.close(); }, []);

  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const prefillFromLicense = (fields: Record<string, any>) => {
    const lic = fromLicense(fields);
    setForm((f) => {
      const merged = { ...f };
      (Object.keys(lic) as (keyof Form)[]).forEach((k) => { if (!merged[k] && lic[k]) merged[k] = lic[k] as string; });
      return merged;
    });
  };

  const startPhoneCapture = async () => {
    setError('');
    try {
      const sid = await api.createMobileLicenseSession();
      setPhoneSid(sid);
      esRef.current?.close();
      const es = new EventSource(api.mobileLicenseEventsUrl(sid));
      esRef.current = es;
      es.addEventListener('photo', async (e: MessageEvent) => {
        try {
          const payload = JSON.parse(e.data);
          const side: 'front' | 'back' = payload?.side === 'back' ? 'back' : 'front';
          if (payload?.fields) {
            if (side === 'front') setFrontOcr(payload.fields); else setBackBarcode(payload.fields);
            prefillFromLicense(payload.fields);
          }
          const file = await api.getMobileLicenseImage(sid, side);
          if (file) { if (side === 'front') setFrontFile(file); else setBackFile(file); }
        } catch { /* ignore a malformed event */ }
      });
      es.addEventListener('expired', () => es.close());
    } catch {
      setError(t('auth.phoneCaptureFailed', 'Could not start phone capture. Please upload from this device.'));
    }
  };

  const continueFromEmail = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !/.+@.+\..+/.test(email)) { setError(t('auth.emailInvalid', 'Enter a valid email.')); return; }
    setBusy(true); setError('');
    try {
      const free = await api.isEmailFree(email);
      setForm((f) => ({ ...f, email }));
      setStep(free ? 'license' : 'password');
    } catch {
      setStep('password'); // on uncertainty, let them try to sign in
    } finally {
      setBusy(false);
    }
  };

  const doSignIn = async () => {
    setBusy(true); setError('');
    try { await signIn(form.email.trim().toLowerCase(), password); onAuthed(); onClose(); }
    catch (e: any) { setError(e?.message || t('login.failed', 'Sign in failed.')); }
    finally { setBusy(false); }
  };

  const onPickLicense = async (file: File, side: 'front' | 'back') => {
    if (side === 'front') setFrontFile(file); else setBackFile(file);
    setReading(side); setError('');
    try {
      const data = await api.readLicense(file, side);
      if (data) {
        if (side === 'front') setFrontOcr(data); else setBackBarcode(data);
        const lic = fromLicense(data);
        // Fill empty fields from the license; never overwrite what the user already typed.
        setForm((f) => {
          const merged = { ...f };
          (Object.keys(lic) as (keyof Form)[]).forEach((k) => { if (!merged[k] && lic[k]) merged[k] = lic[k] as string; });
          return merged;
        });
      }
    } finally {
      setReading(null);
    }
  };

  const createDriver = async (code?: string) => {
    if (!form.firstName.trim() || !form.lastName.trim()) { setError(t('auth.nameRequired', 'First and last name are required.')); return; }
    if (!form.password || form.password.length < 6) { setError(t('auth.passwordShort', 'Password must be at least 6 characters.')); return; }
    if (form.password !== form.confirmPassword) { setError(t('auth.passwordMismatch', 'Passwords do not match.')); return; }
    setBusy(true); setError('');
    try {
      const user = await api.createUserWithLicense({
        FirstName: form.firstName, LastName: form.lastName, Email: form.email,
        PhoneNumber: form.phoneNumber, DateOfBirth: form.dateOfBirth, Address: form.address,
        City: form.city, State: form.state, ZipCode: form.zipCode,
        DriverLicenseId: form.driverLicenseId, DriverLicenseIssuingState: form.driverLicenseIssuingState,
        DriverLicenseExpirationDate: form.driverLicenseExpirationDate, Password: form.password,
        // Full OCR/barcode payloads → backend persists original-language + non-US fields.
        FrontOcrJson: frontOcr ? JSON.stringify(frontOcr) : '',
        BackBarcodeJson: backBarcode ? JSON.stringify(backBarcode) : '',
        PhoneCode: code ?? '',
      }, frontFile, backFile);
      applyAuth(user);
      onAuthed(); onClose();
    } catch (e: any) {
      if (e?.reason === 80301) {
        // The details belong to an existing account — sign in to it instead.
        setCodePrompt(null);
        setStep('password');
        setError(t('auth.accountExists', 'An account with these details already exists. Sign in, or reset your password if you do not remember it.'));
      } else if (e?.reason === 80302 || e?.reason === 80303) {
        setCodePrompt({ failed: e.reason === 80303 });
        setError('');
      } else {
        setError(e?.message || t('auth.createFailed', 'Could not create your account.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="dialog-card w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"><X size={20} /></button>
        {step !== 'email' && (
          <button onClick={() => { setStep('email'); setError(''); }} className="absolute top-3 left-3 text-gray-400 hover:text-gray-600"><ArrowLeft size={20} /></button>
        )}

        {error && <div className="mt-6 mb-3 text-sm rounded-lg bg-red-50 text-red-700 px-3 py-2">{error}</div>}

        {step === 'email' && (
          <div className="mt-2">
            <h2 className="text-xl font-bold text-gray-900">{t('auth.emailTitle', 'Complete your booking')}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">{t('auth.emailSubtitle', 'Enter your email to sign in or create a driver account.')}</p>
            <input type="email" value={form.email} autoFocus
              onChange={(e) => set('email', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && continueFromEmail()}
              placeholder={t('login.email', 'Email')}
              className="w-full px-3 py-2.5 border rounded-lg" />
            <button onClick={continueFromEmail} disabled={busy} className="btn-primary w-full mt-4 py-2.5">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : t('auth.continue', 'Continue')}
            </button>
          </div>
        )}

        {step === 'password' && (
          <div className="mt-2">
            <h2 className="text-xl font-bold text-gray-900">{t('login.title', 'Sign in')}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">{form.email}</p>
            <input type="password" value={password} autoFocus
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSignIn()}
              placeholder={t('login.password', 'Password')}
              className="w-full px-3 py-2.5 border rounded-lg" />
            <button onClick={doSignIn} disabled={busy} className="btn-primary w-full mt-4 py-2.5">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : t('login.signIn', 'Sign in')}
            </button>
          </div>
        )}

        {step === 'license' && (
          <div className="mt-2">
            <h2 className="text-xl font-bold text-gray-900">{t('auth.licenseTitle', 'Driver license')}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">{t('auth.licenseSubtitle', 'Upload your license — we read it to fill your details.')}</p>
            <div className="grid grid-cols-2 gap-3">
              <LicenseDrop label={t('auth.licenseFront', 'Front')} file={frontFile} reading={reading === 'front'} onPick={(f) => onPickLicense(f, 'front')} />
              <LicenseDrop label={t('auth.licenseBack', 'Back')} file={backFile} reading={reading === 'back'} onPick={(f) => onPickLicense(f, 'back')} />
            </div>

            {phoneSid ? (
              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 flex flex-col items-center text-center">
                <div className="bg-white p-2 rounded-lg border">
                  <QRCodeSVG value={api.mobileLicensePageUrl(phoneSid)} size={144} />
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  {t('auth.phoneScanHint', 'Scan with your phone to take photos. They appear here automatically.')}
                </p>
                {(frontFile || backFile) && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    {frontFile && backFile
                      ? t('auth.phoneBothReceived', 'Both photos received.')
                      : t('auth.phoneOneReceived', 'Photo received — waiting for the other side…')}
                  </p>
                )}
              </div>
            ) : (
              <button onClick={startPhoneCapture} className="mt-3 w-full py-2.5 rounded-lg border border-gray-300 font-semibold flex items-center justify-center gap-2 hover:border-yellow-500">
                <Smartphone size={18} /> {t('auth.usePhoneCamera', 'Use Phone Camera')}
              </button>
            )}

            <button onClick={() => setStep('info')} className="btn-primary w-full mt-4 py-2.5">{t('auth.continue', 'Continue')}</button>
            <button onClick={() => setStep('info')} className="w-full mt-2 text-sm text-gray-500 hover:underline">{t('auth.skipLicense', 'Skip for now')}</button>
          </div>
        )}

        {step === 'info' && (
          <div className="mt-2">
            <h2 className="text-xl font-bold text-gray-900">{t('auth.infoTitle', 'Your details')}</h2>
            <p className="text-sm text-gray-500 mt-1 mb-3">{t('auth.infoSubtitle', 'Confirm your information and set a password.')}</p>
            <div className="grid grid-cols-2 gap-2.5">
              <Field ph={t('auth.firstName', 'First name')} v={form.firstName} on={(v) => set('firstName', v)} />
              <Field ph={t('auth.lastName', 'Last name')} v={form.lastName} on={(v) => set('lastName', v)} />
              <Field ph={t('auth.phone', 'Phone')} v={form.phoneNumber} on={(v) => set('phoneNumber', v)} />
              <Field ph={t('auth.dob', 'Date of birth')} type="date" v={form.dateOfBirth} on={(v) => set('dateOfBirth', v)} />
              <Field ph={t('auth.address', 'Address')} v={form.address} on={(v) => set('address', v)} full />
              <Field ph={t('auth.city', 'City')} v={form.city} on={(v) => set('city', v)} />
              <div className="grid grid-cols-2 gap-2.5">
                <Field ph={t('auth.state', 'State')} v={form.state} on={(v) => set('state', v)} />
                <Field ph={t('auth.zip', 'ZIP')} v={form.zipCode} on={(v) => set('zipCode', v)} />
              </div>
              <Field ph={t('auth.dlNumber', 'License #')} v={form.driverLicenseId} on={(v) => set('driverLicenseId', v)} />
              <Field ph={t('auth.dlExp', 'License expiry')} type="date" v={form.driverLicenseExpirationDate} on={(v) => set('driverLicenseExpirationDate', v)} />
              <Field ph={t('login.password', 'Password')} type="password" v={form.password} on={(v) => set('password', v)} />
              <Field ph={t('auth.confirmPassword', 'Confirm password')} type="password" v={form.confirmPassword} on={(v) => set('confirmPassword', v)} />
            </div>
            {codePrompt && (
              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-blue-900">{t('auth.phoneCodeTitle', 'Confirm your phone')}</p>
                <p className="text-sm text-blue-800 mt-1">{t('auth.phoneCodeBody', 'We texted a 6-digit code to the phone number on your account. Enter it to continue.')}</p>
                {codePrompt.failed && <p className="text-sm text-red-600 mt-1">{t('auth.phoneCodeWrong', 'The code is not correct or has expired.')}</p>}
                <div className="flex gap-2 mt-2">
                  <input inputMode="numeric" autoComplete="one-time-code" value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('auth.phoneCodeLabel', 'Verification code')}
                    aria-label={t('auth.phoneCodeLabel', 'Verification code')}
                    className="flex-1 px-3 py-2 border rounded-lg tracking-widest" />
                  <button onClick={() => createDriver(phoneCode)} disabled={busy || phoneCode.length !== 6} className="btn-primary px-4 py-2">
                    {t('auth.phoneCodeConfirm', 'Confirm')}
                  </button>
                </div>
                <button onClick={() => createDriver()} disabled={busy} className="text-sm text-blue-600 hover:underline mt-2">
                  {t('auth.phoneCodeResend', 'Send a new code')}
                </button>
              </div>
            )}
            <button onClick={() => createDriver()} disabled={busy} className="btn-primary w-full mt-4 py-2.5">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : t('auth.createAccount', 'Create account & continue')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ ph, v, on, type = 'text', full }: { ph: string; v: string; on: (v: string) => void; type?: string; full?: boolean }) {
  return (
    <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={ph}
      className={`px-3 py-2 border rounded-lg text-sm ${full ? 'col-span-2' : ''}`} />
  );
}

function LicenseDrop({ label, file, reading, onPick }: { label: string; file: File | null; reading: boolean; onPick: (f: File) => void }) {
  return (
    <label className="border-2 border-dashed border-gray-300 rounded-lg p-3 flex flex-col items-center justify-center text-center cursor-pointer hover:border-yellow-500 min-h-[96px]">
      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
      {reading ? <Loader2 size={20} className="animate-spin text-yellow-600" />
        : file ? <CheckCircle2 size={20} className="text-green-500" />
        : <UploadCloud size={20} className="text-gray-400" />}
      <span className="text-xs text-gray-600 mt-1">{file ? file.name.slice(0, 16) : label}</span>
    </label>
  );
}
